import * as React from "react";
import { UseQueryResult } from "@tanstack/react-query";

import { getApiErrorMessage } from "api/apiError";
import { FleetHistory, FleetHostPoint, FleetSelf, FleetServer, FleetThresholds } from "api/fleet";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "Components/ui";
import { Gauge, Hourglass } from "Components/ui/icons";
import { cn } from "utils/cn";
import { formatDuration, formatTimeOfDay } from "../format";
import LoadChart, { LoadLegend } from "./LoadChart";
import UsageMeter from "./UsageMeter";

const gb = (mb: number): string => `${(mb / 1024).toFixed(1)} GB`;

/** "30 s" or "5 min": how long a CPU figure averages over. */
const windowLabel = (seconds: number): string =>
  seconds < 90 ? `${Math.round(seconds)} s` : `${Math.round(seconds / 60)} min`;

interface SelfAverage {
  pct: number;
  /** First sample's time when SAP's own figures don't cover the full day yet. */
  since: string | null;
}

/** SAP's average share of the server's CPU over the history's samples that carry it. */
const averageSelf = (points: FleetHostPoint[]): SelfAverage | null => {
  const carrying = points.filter(
    (point): point is FleetHostPoint & { selfCpuPct: number } =>
      typeof point.selfCpuPct === "number"
  );
  if (carrying.length === 0) {
    return null;
  }
  const pct = carrying.reduce((sum, point) => sum + point.selfCpuPct, 0) / carrying.length;
  const first = carrying[0].at;
  const partial = Date.now() - new Date(first).getTime() < 23 * 60 * 60 * 1000;
  return { pct, since: partial ? formatTimeOfDay(first) : null };
};

/** "SAP itself · 0.3% of the CPU · 24 h avg 0.2% · 182 MB · event loop p99 12 ms · pm2 read 320 ms". */
const SelfUsageLine: React.FC<{ self: FleetSelf; average: SelfAverage | null }> = ({
  self,
  average,
}) => {
  const parts: string[] = [];
  if (typeof self.cpuPctOfHost === "number") {
    parts.push(`${self.cpuPctOfHost.toFixed(1)}% of the CPU`);
  }
  if (average) {
    parts.push(
      average.since
        ? `avg ${average.pct.toFixed(1)}% since ${average.since}`
        : `24 h avg ${average.pct.toFixed(1)}%`
    );
  }
  parts.push(`${Math.round(self.rssMb)} MB`);
  if (typeof self.eventLoopP99Ms === "number") {
    parts.push(`event loop p99 ${Math.round(self.eventLoopP99Ms)} ms`);
  }
  if (typeof self.pm2ReadMs === "number") {
    parts.push(`pm2 read ${self.pm2ReadMs} ms`);
  }
  return (
    <div className="space-y-0.5">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">SAP itself</span>
        {parts.map((part) => (
          <React.Fragment key={part}>
            <span aria-hidden="true" className="text-subtle-foreground">
              ·
            </span>
            <span>{part}</span>
          </React.Fragment>
        ))}
      </p>
      <p className="text-xs text-muted-foreground">
        Counts the pm2 and df commands SAP runs, not the pm2 daemon&apos;s work answering them.
      </p>
    </div>
  );
};

/** "Peak CPU 64% at 13:20 · memory 78% at 09:05": the chart's highs, readable without hovering. */
const describePeaks = (points: FleetHostPoint[]): string | null => {
  const peak = (key: "cpuPct" | "memUsedPct") =>
    points.reduce<FleetHostPoint | null>((best, point) => {
      const value = point[key];
      if (typeof value !== "number") {
        return best;
      }
      const bestValue = best ? best[key] : null;
      return typeof bestValue !== "number" || value > bestValue ? point : best;
    }, null);
  const cpu = peak("cpuPct");
  const mem = peak("memUsedPct");
  const parts = [
    cpu && typeof cpu.cpuPct === "number"
      ? `CPU ${cpu.cpuPct.toFixed(0)}% at ${formatTimeOfDay(cpu.at)}`
      : null,
    mem && typeof mem.memUsedPct === "number"
      ? `memory ${mem.memUsedPct.toFixed(0)}% at ${formatTimeOfDay(mem.at)}`
      : null,
  ].filter(Boolean);
  return parts.length > 0 ? `Peaks: ${parts.join(" · ")} IST` : null;
};

interface ServerCardProps {
  server: FleetServer | undefined;
  /** SAP's own usage from the overview (null or absent when the server can't be read). */
  self?: FleetSelf | null;
  /** Dashboard limits in effect (SAP settings). */
  thresholds?: FleetThresholds;
  history: UseQueryResult<FleetHistory>;
  loading: boolean;
  className?: string;
}

/** The server every tenant runs on: live CPU, memory and disk, then the last 24 hours. */
const ServerCard: React.FC<ServerCardProps> = ({
  server,
  self,
  thresholds,
  history,
  loading,
  className,
}) => {
  const limits = thresholds
    ? { warnPct: thresholds.usageWarnPct, criticalPct: thresholds.usageCriticalPct }
    : undefined;
  const live = server?.source === "local";
  const points = history.data?.hostSeries || [];
  // Until the sampler has a full day, say where the chart starts instead of implying 24 h.
  const first = points.length > 0 ? points[0].at : null;
  const since =
    first && Date.now() - new Date(first).getTime() < 23 * 60 * 60 * 1000
      ? formatTimeOfDay(first)
      : null;
  const pm2 = live ? server?.pm2 : null;

  let current: React.ReactNode;
  if (loading) {
    current = (
      <div className="grid gap-6 sm:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-1.5 w-full" />
          </div>
        ))}
      </div>
    );
  } else if (!server || !live) {
    current = (
      <EmptyState
        compact
        icon={<Gauge />}
        title="Live server metrics unavailable"
        description={server?.reason || "SAP can't read this server's metrics."}
      />
    );
  } else {
    current = (
      <div className="grid gap-6 sm:grid-cols-3">
        <UsageMeter
          limits={limits}
          label="CPU"
          value={server.cpuPct}
          color="chart-1"
          detail={
            server.load && server.cores
              ? [
                  typeof server.cpuWindowSec === "number"
                    ? `${windowLabel(server.cpuWindowSec)} average`
                    : null,
                  `load ${server.load[0].toFixed(2)} on ${server.cores} cores`,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : undefined
          }
        />
        <UsageMeter
          limits={limits}
          label="Memory"
          value={server.mem ? server.mem.usedPct : null}
          color="chart-2"
          detail={server.mem ? `of ${gb(server.mem.totalMb)}` : undefined}
        />
        <UsageMeter
          limits={limits}
          label="Disk"
          value={server.disk ? server.disk.usedPct : null}
          color="chart-6"
          detail={server.disk ? `of ${server.disk.totalGb.toFixed(0)} GB` : undefined}
        />
      </div>
    );
  }

  // "pm2 · 40 online · 41 stopped · 2 errored: admin, sync": what runs on the box, by state.
  const pm2Parts: React.ReactNode[] = [];
  if (pm2) {
    const other = pm2.total - pm2.online - pm2.stopped - pm2.errored;
    pm2Parts.push(`${pm2.online} online`, `${pm2.stopped} stopped`);
    if (pm2.errored > 0) {
      pm2Parts.push(
        <span key="errored">
          {pm2.errored} errored
          {pm2.erroredApps && pm2.erroredApps.length > 0 && (
            <>
              : <span className="font-mono text-foreground">{pm2.erroredApps.join(", ")}</span>
            </>
          )}
        </span>
      );
    }
    if (other > 0) {
      pm2Parts.push(`${other} starting or other`);
    }
  }

  let chart: React.ReactNode;
  if (history.isLoading) {
    chart = <Skeleton className="h-48 w-full" />;
  } else if (history.isError) {
    chart = (
      <ErrorState
        compact
        title="Server history didn't load"
        description={getApiErrorMessage(history.error)}
      />
    );
  } else if (points.length < 2) {
    chart = (
      <EmptyState
        compact
        icon={<Hourglass />}
        title="Collecting data"
        description="The chart fills in as SAP samples the server every few minutes."
      />
    );
  } else {
    const peaks = describePeaks(points);
    chart = (
      <>
        <LoadChart points={points} />
        {peaks && <p className="mt-2 text-xs text-muted-foreground">{peaks}</p>}
      </>
    );
  }

  return (
    <Card className={className}>
      <CardHeader
        title="Server"
        description={
          live && server ? (
            <>
              <span className="font-mono">{server.host}</span>
              {server.uptimeSec !== null && ` · up ${formatDuration(server.uptimeSec)}`}
            </>
          ) : (
            "Shared by the TeleOpd tenants"
          )
        }
      />
      <div className="space-y-6 px-5 pb-5 pt-5">
        {current}
        {pm2Parts.length > 0 && (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              {pm2 ? `${pm2.total} pm2 apps` : "pm2"}
            </span>
            {pm2Parts.map((part, index) => (
              <React.Fragment key={index}>
                <span aria-hidden="true" className="text-subtle-foreground">
                  ·
                </span>
                {typeof part === "string" ? <span>{part}</span> : part}
              </React.Fragment>
            ))}
          </p>
        )}
        {live && self && <SelfUsageLine self={self} average={averageSelf(points)} />}
        <div className={cn(!loading && live && "border-t border-border pt-5")}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-medium">
              Last 24 hours
              {since && (
                <span className="font-normal text-muted-foreground"> · data since {since} IST</span>
              )}
            </h4>
            {points.length >= 2 && <LoadLegend />}
          </div>
          {chart}
        </div>
      </div>
    </Card>
  );
};

export default ServerCard;
